import React, { useMemo, useCallback, useEffect, useContext } from 'react'
import CartReviewTile, { LoyaltyPointsEarningConfiguration } from './CartReviewTile'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import moment from 'moment'
import CartReviewPriceStack from './CartReviewPriceStack'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'
import Divider from 'components/Luxkit/Divider'
import { useUserCartItemState } from 'hooks/UserCart/useUserCartItemState'
import FlightCartReviewTileExtension from './FlightCartReviewTileExtension'
import { FlightViewTypes } from 'constants/flight'
import styled from 'styled-components'
import useRefreshFlightItem from 'hooks/UserCart/useRefreshFlightItem'
import Group from 'components/utils/Group'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import BodyText from 'components/Luxkit/Typography/BodyText'
import TextLink from 'components/Luxkit/TextLink'
import { buildStandaloneFlightSearchUrl, getFlightLoyaltyProductType, getFlightLoyaltyProductTypeRank, getFlightsTotal } from 'lib/flights/flightUtils'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { userCartItemsDelete, userCartItemsSaveForLater } from 'actions/AuthActions'
import { getUserCartItemsArray } from 'selectors/userCart'
import { isFlightItem } from 'lib/checkout/checkoutUtils'
import ExpiryTimer from './ExpiryTimer'
import { dateIsBeforeOrSame } from 'lib/datetime/dateUtils'
import { rem } from 'polished'
import { multiCartDeleteItemEvent, multiCartSaveItemForLaterEvent } from 'analytics/snowplow/events'
import GeoContext from 'contexts/geoContext'
import { arrayToMap, sortBy } from 'lib/array/arrayUtils'

const ExtensionContainer = styled(Group)`
  grid-area: extension;
`

const DividerPadding = styled.div`
  padding-inline: ${rem(20)};
`

interface Props {
  item: App.Checkout.FlightItem
  tileVariant?: 'default' | 'condensed'
}

function FlightCartReviewTile({ item, tileVariant = 'default' }: Props) {
  const dispatch = useAppDispatch()
  const { isDeleted, isSavedForLater } = useUserCartItemState(item.itemId)
  const cartItems = useAppSelector(getUserCartItemsArray)
  const flightItems = cartItems.filter(cartItem => isFlightItem(cartItem))
  const cartState = useAppSelector(state => state.auth.userCart.itemStates)
  const { dataStatus, initiateRefresh } = useRefreshFlightItem(item)
  const failedRefresh = dataStatus === 'refreshFailed'
  const isPast = dateIsBeforeOrSame(new Date(item.flights[0].departingDate), new Date())
  const hasExpired = dataStatus === 'expired'
  const isAvailable = !failedRefresh && !hasExpired && !isPast
  const isBookable = !isDeleted && isAvailable
  const canShowExtensionAndTimer = !!item.expiry && dataStatus !== 'refreshFailed'
  const flightSearchUrl = useMemo(() => buildStandaloneFlightSearchUrl({
    originAirportCode: item.originAirportCode,
    originAirportName: item.flights[0].departingAirportName,
    destinationAirportCode: item.destinationAirportCode,
    destinationAirportName: item.flights[0].arrivalAirportName,
    departDate: item.flights[0].departingDate,
    returnDate: item.flights.length > 1 ? item.flights[1].departingDate : null,
    adults: item.occupants.adults,
    children: item.occupants.children ?? 0,
    infants: item.occupants.infants ?? 0,
    childrenAge: item.occupants.childrenAge?.join(',') ?? '',
    fareClass: item.flights[0].fareFamily?.cabin?.toLowerCase() ?? 'economy',
    fareType: item.fareType,
  }), [
    item.originAirportCode,
    item.flights,
    item.destinationAirportCode,
    item.occupants,
    item.fareType,
  ])
  const { airports } = useContext(GeoContext)
  const hasActiveFlightsInCart = useMemo(() => {
    return flightItems.some(flightItem => !cartState[flightItem.itemId]?.saveForLater)
  }, [flightItems, cartState])

  useEffect(() => {
    if (!isAvailable && !isSavedForLater && !isDeleted) {
      dispatch(userCartItemsSaveForLater([item.itemId]))
    }
  }, [isAvailable, isSavedForLater, dispatch, item.itemId, isDeleted])

  const formattedOccupancy = useMemo(() => {
    const hasInfants = item.occupants.infants && item.occupants.infants > 0
    const formattedOccupants = formatOccupantsShort(item.occupants)
    return `${formattedOccupants} ${hasInfants ? '(on lap)' : ''}`
  }, [item.occupants])

  const title = useMemo(() => {
    switch (item.viewType) {
      case FlightViewTypes.ONE_WAY:
        return 'One-way flight'
      case FlightViewTypes.TWO_ONE_WAYS_AND_RETURN:
      case FlightViewTypes.TWO_ONE_WAYS:
        return 'Return flights'
      case FlightViewTypes.MULTI_CITY:
        return 'Multi-city flights'
      default:
        return ''
    }
  }, [item.viewType])

  const dates = useMemo(() => {
    const firstFlight = item.flights[0]
    if (!firstFlight.departingDate || !firstFlight.departingAirportName || !firstFlight.arrivalAirportName) {
      return ''
    }

    const date = moment(firstFlight.departingDate).format('D MMM YYYY')

    switch (item.viewType) {
      case FlightViewTypes.ONE_WAY:
        return `${date} • ${firstFlight.departingAirportName} to ${firstFlight.arrivalAirportName}`
      case FlightViewTypes.TWO_ONE_WAYS_AND_RETURN:
      case FlightViewTypes.TWO_ONE_WAYS:
      case FlightViewTypes.MULTI_CITY: {
        const lastFlight = item.flights[item.flights.length - 1]
        if (!lastFlight.departingDate || !lastFlight.arrivalAirportName) {
          return ''
        }

        const endDate = moment(lastFlight.departingDate).format('D MMM YYYY')
        const fromCity = firstFlight.departingAirportName
        const toCity = item.viewType === FlightViewTypes.MULTI_CITY ? lastFlight.arrivalAirportName : firstFlight.arrivalAirportName

        return `${date} - ${endDate} • ${fromCity} to ${toCity}`
      }
      default:
        return ''
    }
  }, [item.flights, item.viewType])

  const totalCost = useMemo(() => getFlightsTotal(item), [item])

  const bannerLabelVariant = useMemo(() => {
    if (isPast) return 'pastDates'
    if (hasExpired) return 'expired'
    if (failedRefresh) return 'failedRefresh'
    if (isBookable && isSavedForLater && hasActiveFlightsInCart) return 'bookingLimit'
    return null
  }, [hasExpired, failedRefresh, isBookable, isSavedForLater, hasActiveFlightsInCart, isPast])

  const getBannerContent = useCallback((variant: string) => {
    switch (variant) {
      case 'pastDates':
        return (
          <Group direction="horizontal" gap={12}>
            <BodyText variant="medium">Your selected dates are in the past.</BodyText>
            <TextLink weight="bold" to="/flights" target="_blank">
              Check availability
            </TextLink>
          </Group>
        )
      case 'expired':
        return (
          <Group direction="horizontal" gap={12}>
            <BodyText variant="medium">This flight price has expired</BodyText>
            <TextLink weight="bold" onClick={initiateRefresh}>
              Refresh search
            </TextLink>
          </Group>
        )
      case 'failedRefresh':
        return (
          <Group direction="horizontal" gap={12}>
            <BodyText variant="medium">We couldn't refresh your flight.</BodyText>
            <TextLink weight="bold" to={flightSearchUrl} target="_blank">
              Search for flights
            </TextLink>
          </Group>
        )
      case 'bookingLimit':
        return 'Please note, only one flight itinerary can be booked per transaction'
      default:
        return ''
    }
  }, [initiateRefresh, flightSearchUrl])

  const banner = useMemo(() => {
    if (!bannerLabelVariant) return null

    return (
      <MessageBanner
        kind="warning"
        description={getBannerContent(bannerLabelVariant)}
        density="compact"
        icon={<LineInfoCircleIcon />}
      />
    )
  }, [bannerLabelVariant, getBannerContent])

  const handleSaveForLater = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    multiCartSaveItemForLaterEvent(item, isSavedForLater)

    if (isSavedForLater) {
      // First make this flight active
      dispatch(userCartItemsSaveForLater([item.itemId]))
      // Then change all other flights to inactive
      flightItems.forEach(flightItem => {
        if (flightItem.itemId !== item.itemId && !cartState[flightItem.itemId]?.saveForLater) {
          dispatch(userCartItemsSaveForLater([flightItem.itemId]))
        }
      })
    } else {
      dispatch(userCartItemsSaveForLater([item.itemId]))
    }
  }, [dispatch, item, isSavedForLater, cartState, flightItems])

  const handleDelete = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    multiCartDeleteItemEvent(item)
    dispatch(userCartItemsDelete([item.itemId]))
  }, [dispatch, item])

  const extension = useMemo(() => {
    if (!canShowExtensionAndTimer) return null

    return (
      <ExtensionContainer direction="vertical" gap={8}>
        <DividerPadding><Divider kind="primary" /></DividerPadding>
        {item.flights.map((flight, index) => (
          <React.Fragment key={flight.journeyId}>
            <FlightCartReviewTileExtension flight={flight} />
            {index < item.flights.length - 1 && <DividerPadding><Divider kind="primary" /></DividerPadding>}
          </React.Fragment>
        ))}
      </ExtensionContainer>
    )
  }, [canShowExtensionAndTimer, item.flights])

  const loyaltyPointsEarningConfiguration = useMemo<LoyaltyPointsEarningConfiguration>(() => {
    const airportsByCode = arrayToMap(airports, airport => airport.code)

    const luxProductTypes = item.flights.map(flight => {
      const departingAirport = airportsByCode.get(flight.departingAirportCode)
      const arrivalAirport = airportsByCode.get(flight.arrivalAirportCode)

      const isDomestic = departingAirport?.countryCode === arrivalAirport?.countryCode
      return getFlightLoyaltyProductType(flight.fareFamily?.cabin?.toLowerCase() ?? 'economy', isDomestic)
    })

    const rankedLuxProductTypes = sortBy(luxProductTypes, productType => getFlightLoyaltyProductTypeRank(productType), 'asc')

    return {
      calculationRequests: [{
        loyaltyProductType: rankedLuxProductTypes[0],
        price: totalCost,
      }],
      calculationType: 'exact',
    }
  }, [airports, item.flights, totalCost])

  return (
    <CartReviewTile
      itemId={item.itemId}
      fetching={dataStatus === 'refreshing'}
      label={
        <ExpiryTimer
          canShowExtension={canShowExtensionAndTimer}
          expiry={item.expiry ?? 0}
        />
      }
      title={title}
      dates={dates}
      occupancy={formattedOccupancy}
      loyaltyPointsEarningConfiguration={loyaltyPointsEarningConfiguration}
      price={
        <CartReviewPriceStack
          price={totalCost}
          value={totalCost}
          luxPlusOfferConfig={{}}
        />
      }
      banner={banner}
      extension={extension}
      onSaveForLater={handleSaveForLater}
      onDelete={handleDelete}
      saveForLater={isSavedForLater}
      deleted={isDeleted}
      isBookable={isBookable}
      tileVariant={tileVariant}
    />
  )
}

export default FlightCartReviewTile
